import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ENUM_ROUTES } from "../../RoutesEnum";
import TableComponent from "../../components/TableComponent";
import { useLazyGetRefundsQuery, useUpdateRefundMutation } from "../../services/refund";
import { showError, showToast } from "../../constants/toasts";
import { ConfirmationModal } from "../../Modals/ConfirmationModal";
import moment from "moment";
import { Box, Button } from "@mui/material";
import DownloadIcon from '@mui/icons-material/Download';
import SearchBar from "../../components/SearchBar";
import { isValidInput } from "../../utils/validations";


const RefundList = () => {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState("");
  const [page, setPage] = useState(1);
  const [pageSize] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [refundData, setRefundData] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedId, setSelectedId] = useState("");
  const [open, setOpen] = useState(false);
  const [actionType, setActionType] = useState<"approve" | "reject" | "process">("approve");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState<string>("");
  const [getRefunds] = useLazyGetRefundsQuery();
  const [updateRefund, { isLoading: isUpdating }] = useUpdateRefundMutation();

  const fetchRefunds = async () => {
    try {
      setLoading(true);
      const response = await getRefunds({
        from: page,
        size: pageSize,
        search: debouncedSearchTerm,
      }).unwrap();

      if (response?.statusCode === 200) {
        setRefundData(response.data.data || []);
        setTotalCount(response.data.total || 0);
      }
    } catch (error: any) {
      showError(error?.data?.message || "Failed to fetch refunds");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    fetchRefunds();
  }, [page, searchTerm, debouncedSearchTerm]);



  const currency = (val: number) => `AED ${val.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

  const handleRefund = async (row: any) => {
    try {
      setLoading(true);
      const bookingId = row?.bookingId || row?._id;
      const response = await updateRefund({ body: { bookingId: String(bookingId) } }).unwrap();
      if (response?.statusCode === 200) {
        showToast(response?.message || "Refund processed successfully");
        fetchRefunds();
      }
    } catch (error: any) {
      showError(error?.data?.message || "Failed to process refund");
    } finally {
      setLoading(false);
    }
  };
  const columns = [
    {
      flex: 1,
      minWidth: 280,
      field: "user",
      headerName: "Requested By (User Details)",
      editable: false,
      sortable: false,
      renderCell: (params: any) => {
        const avatar = params?.row?.avatar;
        const name = params?.row?.name;
        const email = params?.row?.email;
        const phone = params?.row?.phone;
        return (
          <div className="user_block">
            <figure>
              <img src={avatar} alt="" />
            </figure>
            <div>
              <p><b>{name}</b></p>
              <p><span>{email}</span></p>
              <p><span>{phone}</span></p>
            </div>
          </div>
        );
      },
    },
    {
      minWidth: 260,
      field: "transactionId",
      headerName: "Transaction ID",
      editable: false,
      sortable: false,
      align: "center",
      headerAlign: "center",
      renderCell: (params: any) => (
        <p className="c_primary"><strong>{params?.row?.transactionId || '-'}</strong></p>
      ),
    },
    {
      minWidth: 180,
      field: "startDate",
      headerName: "Start Date",
      editable: false,
      sortable: false,
      align: "center",
      headerAlign: "center",
      renderCell: (params: any) => (
        <p className="c_black"><strong>{params?.row?.startDate || '-'}</strong></p>
      ),
    },
    {
      minWidth: 180,
      field: "endDate",
      headerName: "End Date",
      editable: false,
      sortable: false,
      align: "center",
      headerAlign: "center",
      renderCell: (params: any) => (
        <p className="c_black"><strong>{params?.row?.endDate || '-'}</strong></p>
      ),
    },
    {
      flex: 0.6,
      minWidth: 200,
      field: "venueName",
      headerName: "Venue Name",
      editable: false,
      sortable: false,
      renderCell: (params: any) => (
        <p className="c_black"><strong>{params?.row?.venueName || '-'}</strong></p>
      ),
    },
    {
      minWidth: 180,
      field: "amount",
      headerName: "Amount",
      editable: false,
      sortable: false,
      align: "center",
      headerAlign: "center",
      renderCell: (params: any) => (
        <p className="c_success"><strong>{currency(Number(params?.row?.amount) || 0)}</strong></p>
      ),
    },
    {
      minWidth: 200,
      field: "requestDate",
      headerName: "Refund Request Date",
      editable: false,
      sortable: false,
      align: "center",
      headerAlign: "center",
      renderCell: (params: any) => (
        <p className="c_black"><strong>{params?.row?.requestDate || '-'}</strong></p>
      ),
    },
    {
      minWidth: 140,
      field: "status",
      headerName: "Status",
      editable: false,
      sortable: false,
      align: "center",
      headerAlign: "center",
      renderCell: (params: any) => {
        const statusRaw = String(params?.row?.status || '').toLowerCase();
        const statusClass = statusRaw === 'approved' ? 'c_success' : statusRaw === 'rejected' ? 'c_error' : 'c_warning';
        const label = statusRaw.charAt(0).toUpperCase() + statusRaw.slice(1);
        return (
          <span className={`status_badge ${statusClass}`}>
            {label}
          </span>
        );
      },
    },
    {
      minWidth: 160,
      field: "action",
      headerName: "Action",
      editable: false,
      sortable: false,
      align: "center",
      headerAlign: "center",
      renderCell: (params: any) => {
        const statusRaw = String(params?.row?.status || '').toLowerCase();
        if (statusRaw === 'refund' || statusRaw === 'refunded') {
          return <p className="c_success"><strong>Paid</strong></p>;
        }
        if (statusRaw === 'pending') {
          return (
            <Button
              className="btn btn_primary sm"
              disabled={isUpdating}
              onClick={() => handleRefund(params?.row)}
            >
              Pay
            </Button>
          );
        }
        return <span>-</span>;
      },
    },
  ];

  const mappedRows = refundData.map((refund) => {
    const firstName = refund?.userDetails?.firstName || "";
    const lastName = refund?.userDetails?.lastName || "";
    const email = refund?.userDetails?.email || "-";
    const countryCode = refund?.userDetails?.countryCode || "";
    const phoneNumber = refund?.userDetails?.phoneNumber || "";
    const phone = countryCode && phoneNumber ? `${countryCode} ${phoneNumber}` : (phoneNumber || "-");

    const format = (iso?: string) => (iso ? moment(iso).format("DD MMM YYYY, hh:mm A") : undefined);
    return {
      _id: refund?._id,
      name: `${firstName} ${lastName}`.trim() || "-",
      email,
      phone,
      avatar: refund?.userDetails?.image || "/static/images/user_placeholder.png",
      amount: Number(refund?.amount) || 0,
      transactionId: refund?.transactionId || "-",
      startDate: format(refund?.bookingDetails?.from) || "-",
      endDate: format(refund?.bookingDetails?.to) || "-",
      venueName: refund?.bookingDetails?.venueStorageName || "-",
      requestDate: format(refund?.bookingDetails?.createdAt) || "-",
      status: String(refund?.status || "pending"),
      createdAt: refund?.createdAt ? moment(refund.createdAt).toISOString() : undefined,
      bookingId: refund?.bookingId,
    };
  });

  return (
    <>
      <div className="table_header">
        <div className="left_s">
          <Box className="search_box">
            <SearchBar
              placeholder="Search..."
              searchTerm={searchTerm}
              setDebouncedSearchTerm={setDebouncedSearchTerm}
              value={searchTerm}
              onCross={() => setSearchTerm("")}
              onChange={(val: any) => {
                if (isValidInput(val.target.value)) {
                  setSearchTerm(val.target.value);
                }
              }}
            />
          </Box>
        </div>
        <div className="right_s">
          <Button className="btn btn_grey"><DownloadIcon /> Export</Button>
        </div>
      </div>
      <div className="sorting_table">
        <TableComponent
          columns={columns}
          rows={mappedRows}
          count={totalCount}
          page={page}
          onPageChange={(e: any, newPage: number) => setPage(newPage)}
          loading={loading}
          getRowId={(row: any) => row._id}
        />
        <ConfirmationModal
          text={`Are you sure you want to ${actionType} this refund?`}
          openModal={open}
          setOpenModal={setOpen}
          handleSubmit={() => { }}
        />
      </div>
    </>
  );
};

export default RefundList;
